import { useEffect, useState, useMemo, type DragEvent } from 'react';
import type React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { getPropertyTimezone, toCheckinInstant, toCheckoutInstant } from '../lib/property-time';
import InvoiceModal from '../components/InvoiceModal';
import { Skeleton } from '../components/Skeleton';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import {
  useDashboardQuery,
  usePastGuestsQuery,
  usePropertiesQuery,
  useRefreshGuests,
  useUpdateColumnMutation,
  guestKeys,
  type DashboardData,
  type PastGuest,
} from '../hooks/useGuests';

const COLUMNS = [
  { key: 'arriving_soon', label: 'Arriving Soon', color: 'bg-blue-500' },
  { key: 'current_guest', label: 'Current Guest', color: 'bg-green-500' },
  { key: 'review_period', label: 'Review Period', color: 'bg-amber-500' },
  { key: 'claim_period', label: 'Claim Period', color: 'bg-red-500' },
] as const;

const PLATFORM_BADGES: Record<string, { label: string; class: string }> = {
  airbnb: { label: 'Airbnb', class: 'bg-rose-100 text-rose-700' },
  booking_com: { label: 'Booking', class: 'bg-blue-100 text-blue-700' },
  guesty: { label: 'Guesty', class: 'bg-purple-100 text-purple-700' },
};

const RATING_COLORS: Record<string, { emoji: string; label: string; class: string }> = {
  green: { emoji: '🟢', label: 'Green', class: 'bg-green-100 text-green-800' },
  yellow: { emoji: '🟡', label: 'Yellow', class: 'bg-yellow-100 text-yellow-800' },
  red: { emoji: '🔴', label: 'Red', class: 'bg-red-100 text-red-800' },
};

interface Conversation {
  id: number;
  guest_name: string;
  property_name: string;
  platform: string;
  check_in?: string;
  check_out?: string;
  checkout_at?: string;
  column: string;
  manual_column?: string | null;
  guest_rating?: string;
  claim_id?: number;
  claim_amount?: number;
  claim_status?: string;
  claim_reason?: string;
  review_text?: string;
}

interface Property {
  id: number;
  name: string;
  location?: string;
  timezone?: string;
}

interface NameFilterable {
  guest_name: string;
}

function daysRemaining(conv: Conversation, properties: Property[]) {
  const now = new Date();
  const tz = getPropertyTimezone(conv.property_name, properties);
  if (conv.column === 'arriving_soon') {
    const checkin = toCheckinInstant(conv.check_in, tz);
    if (!checkin) return '';
    const hours = Math.max(0, Math.ceil((checkin.getTime() - now.getTime()) / (1000 * 60 * 60)));
    return hours <= 24 ? `${hours}h until check-in` : `${Math.ceil(hours / 24)}d until check-in`;
  }
  if (conv.column === 'current_guest') {
    const checkout = toCheckoutInstant(conv.check_out, tz);
    if (!checkout) return '';
    const days = Math.max(0, Math.ceil((checkout.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    return `${days}d until checkout`;
  }
  if (conv.column === 'review_period') {
    const checkoutAt = new Date(conv.checkout_at || '');
    const deadline = new Date(checkoutAt.getTime() + 14 * 86400000);
    const days = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    return `${days}d left in review period`;
  }
  if (conv.column === 'claim_period') {
    return 'Review period closed';
  }
  return '';
}

function formatDate(d: string | undefined) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function matchesProperty(conv: Conversation, propertyId: string, properties: Property[]) {
  if (propertyId === 'all') return true;
  const selected = properties.find((p) => String(p.id) === propertyId);
  if (!selected) return true;
  return conv.property_name.trim().toLowerCase() === selected.name.trim().toLowerCase();
}

function matchesName(item: NameFilterable, nameFilter: string) {
  if (!nameFilter) return true;
  if (!item.guest_name) return false;
  return item.guest_name.toLowerCase().includes(nameFilter);
}

function toDateOnly(d: string) {
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function matchesDateRange(
  checkIn: string | undefined,
  checkOut: string | undefined,
  from: string,
  to: string
) {
  if (!from && !to) return true;
  const start = checkIn ? toDateOnly(checkIn) : null;
  const end = checkOut ? toDateOnly(checkOut) : null;
  if (!start || !end) return false;
  const rangeFrom = from ? toDateOnly(from) : null;
  const rangeTo = to ? toDateOnly(to) : null;
  if (rangeFrom && rangeTo) {
    return start <= rangeTo && end >= rangeFrom;
  }
  if (rangeFrom) {
    return end >= rangeFrom;
  }
  if (rangeTo) {
    return start <= rangeTo;
  }
  return true;
}

export default function Guests() {
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [invoiceModalConv, setInvoiceModalConv] = useState<number | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<string>('all');
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const queryClient = useQueryClient();
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboardQuery();
  const { data: properties = [] } = usePropertiesQuery();
  const { data: pastGuests = [], isLoading: pastGuestsLoading } = usePastGuestsQuery(selectedProperty);
  const refreshGuests = useRefreshGuests();
  const updateColumn = useUpdateColumnMutation();
  const showPastGuestsLoading = useDelayedLoading(pastGuestsLoading);

  const data = useMemo(
    () => dashboardData ?? { arriving_soon: [], current_guest: [], review_period: [], claim_period: [] },
    [dashboardData]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  const filteredData = useMemo(() => {
    const result: DashboardData = { arriving_soon: [], current_guest: [], review_period: [], claim_period: [] };
    for (const col of COLUMNS) {
      result[col.key as keyof DashboardData] = data[col.key as keyof DashboardData].filter((conv) =>
        matchesProperty(conv, selectedProperty, properties) &&
        matchesName(conv, debouncedNameFilter) &&
        matchesDateRange(conv.check_in, conv.check_out, dateFrom, dateTo)
      );
    }
    return result;
  }, [data, selectedProperty, properties, debouncedNameFilter, dateFrom, dateTo]);

  const filteredPastGuests = useMemo(() => {
    return pastGuests.filter((g) =>
      matchesName(g, debouncedNameFilter) &&
      matchesDateRange(g.checkin_date, g.checkout_date, dateFrom, dateTo)
    );
  }, [pastGuests, debouncedNameFilter, dateFrom, dateTo]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedProperty !== 'all') count += 1;
    if (debouncedNameFilter) count += 1;
    if (dateFrom || dateTo) count += 1;
    return count;
  }, [selectedProperty, debouncedNameFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setNameFilter('');
    setDebouncedNameFilter('');
    setDateFrom('');
    setDateTo('');
    setSelectedProperty('all');
  };

  const handleDragStart = (e: DragEvent, conv: Conversation) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(conv));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: DragEvent, colKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  };

  const handleDragLeave = () => setDragOverCol(null);

  const handleDrop = async (e: DragEvent, targetCol: string) => {
    e.preventDefault();
    setDragOverCol(null);
    try {
      const conv: Conversation = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (conv.column === targetCol) return;
      // Optimistically update the UI while the mutation persists server-side
      queryClient.setQueryData<DashboardData>(guestKeys.dashboard, (prev) => {
        const base = prev ?? data;
        const updated: DashboardData = { ...base };
        updated[conv.column as keyof DashboardData] = updated[conv.column as keyof DashboardData].filter((c) => c.id !== conv.id);
        updated[targetCol as keyof DashboardData] = [...updated[targetCol as keyof DashboardData], { ...conv, column: targetCol }];
        return updated;
      });
      await updateColumn.mutateAsync({ id: conv.id, manual_column: targetCol });
    } catch (err) {
      console.error('Failed to update column:', err);
      refreshGuests();
    }
  };

  const handleSetRating = async (conv: Conversation, rating: string) => {
    try {
      await api.updateRating(conv.id, rating);
      refreshGuests();
      setSelected((s) => s && s.id === conv.id ? { ...s, guest_rating: rating } : s);
    } catch (err) {
      console.error('Failed to set rating:', err);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Filters */}
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3 shrink-0">
        <label htmlFor="property-filter" className="text-sm font-medium text-gray-700">
          Property
        </label>
        <select
          id="property-filter"
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
        >
          <option value="all">All</option>
          {properties.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>

        <label htmlFor="name-filter" className="text-sm font-medium text-gray-700 ml-2">
          Guest
        </label>
        <input
          id="name-filter"
          type="text"
          placeholder="Search guest name…"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white min-w-[180px]"
        />

        <label htmlFor="date-from" className="text-sm font-medium text-gray-700 ml-2">
          From
        </label>
        <input
          id="date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
        />

        <label htmlFor="date-to" className="text-sm font-medium text-gray-700">
          To
        </label>
        <input
          id="date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
        />

        {activeFiltersCount > 0 && (
          <button
            onClick={clearFilters}
            className="ml-auto text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Kanban */}
      <div className="flex-1 min-h-0 flex">
        <div className={`flex gap-4 flex-1 overflow-x-auto p-1 transition-all ${selected ? 'pr-96' : ''}`}>
          {COLUMNS.map((col) => {
            const cards = filteredData[col.key as keyof DashboardData] || [];
            return (
              <div
                key={col.key}
                className={`flex-1 min-w-[260px] flex flex-col rounded-lg ${dragOverCol === col.key ? 'bg-gray-200' : 'bg-gray-100'} transition-colors`}
                onDragOver={(e) => handleDragOver(e, col.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.key)}
              >
                <div className="flex items-center gap-2 px-3 py-3">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                  <h2 className="text-sm font-semibold text-gray-700">{col.label}</h2>
                  <span className="ml-auto bg-gray-300 text-gray-700 text-xs font-bold px-2 py-0.5 rounded-full">
                    {cards.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2">
                  {dashboardLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-14" />
                          </div>
                          <Skeleton className="h-3 w-1/2" />
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-7 w-20" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {cards.map((conv) => (
                        <GuestCard
                          key={conv.id}
                          conv={conv}
                          properties={properties}
                          onSelect={() => setSelected(selected?.id === conv.id ? null : conv)}
                          isSelected={selected?.id === conv.id}
                          onDragStart={(e) => handleDragStart(e, conv)}
                          onInvoice={() => setInvoiceModalConv(conv.id)}
                        />
                      ))}
                      {cards.length === 0 && (
                        <div className="text-center text-xs text-gray-400 py-8">No guests</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {selected && (
          <DetailPanel
            key={selected.id}
            conv={selected}
            properties={properties}
            onClose={() => setSelected(null)}
            onSetRating={handleSetRating}
            onRefresh={refreshGuests}
          />
        )}
        {invoiceModalConv && (
          <InvoiceModal
            conversationId={invoiceModalConv}
            open
            onClose={() => setInvoiceModalConv(null)}
            onSent={() => refreshGuests()}
          />
        )}
      </div>

      {/* Past guests */}
      <div className="shrink-0 border-t border-gray-200 max-h-80 overflow-y-auto">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Past Guests</h2>
        </div>
        {showPastGuestsLoading ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Guest</th>
                <th className="px-4 py-2 text-left font-medium">Property</th>
                <th className="px-4 py-2 text-left font-medium">Check-in</th>
                <th className="px-4 py-2 text-left font-medium">Check-out</th>
                <th className="px-4 py-2 text-left font-medium">Total Spent</th>
                <th className="px-4 py-2 text-left font-medium">Platform</th>
                <th className="px-4 py-2 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-24" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-28" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-16" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-4 w-14" /></td>
                  <td className="px-4 py-2"><Skeleton className="h-7 w-20" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : filteredPastGuests.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-gray-400">
            {activeFiltersCount > 0 ? 'No past guests match the filters' : 'No past guests for this property'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Guest</th>
                <th className="px-4 py-2 text-left font-medium">Property</th>
                <th className="px-4 py-2 text-left font-medium">Check-in</th>
                <th className="px-4 py-2 text-left font-medium">Check-out</th>
                <th className="px-4 py-2 text-left font-medium">Total Spent</th>
                <th className="px-4 py-2 text-left font-medium">Platform</th>
                <th className="px-4 py-2 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPastGuests.map((g) => (
                <tr key={g.booking_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{g.guest_name}</td>
                  <td className="px-4 py-2 text-gray-600">{g.property_name}</td>
                  <td className="px-4 py-2 text-gray-600">{formatDate(g.checkin_date)}</td>
                  <td className="px-4 py-2 text-gray-600">{formatDate(g.checkout_date)}</td>
                  <td className="px-4 py-2 text-gray-900">
                    {g.total_spent?.toFixed(2)} {g.currency}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PLATFORM_BADGES[g.platform]?.class || 'bg-gray-100 text-gray-700'}`}>
                      {PLATFORM_BADGES[g.platform]?.label || g.platform}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {g.conversation_id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setInvoiceModalConv(g.conversation_id)}
                          className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          📄 Invoice
                        </button>
                        <a
                          href="/conversations"
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          View conversation &rarr;
                        </a>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function GuestCard({
  conv,
  properties,
  onSelect,
  isSelected,
  onDragStart,
  onInvoice,
}: {
  conv: Conversation;
  properties: Property[];
  onSelect: () => void;
  isSelected: boolean;
  onDragStart: (e: DragEvent) => void;
  onInvoice: () => void;
}) {
  const platform = PLATFORM_BADGES[conv.platform] || { label: conv.platform, class: 'bg-gray-100 text-gray-700' };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onSelect}
      className={`bg-white rounded-lg shadow-sm border p-3 cursor-pointer hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200'}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-gray-900 truncate">{conv.guest_name}</span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${platform.class}`}>
          {platform.label}
        </span>
      </div>
      <div className="text-xs text-gray-500 mb-1.5">{conv.property_name}</div>
      <div className="text-xs text-gray-400 mb-2">
        {formatDate(conv.check_in)} &rarr; {formatDate(conv.check_out)}
      </div>
      <div className="text-xs font-medium text-gray-600 mb-1.5">{daysRemaining(conv, properties)}</div>
      {conv.column === 'review_period' && conv.guest_rating && (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${RATING_COLORS[conv.guest_rating].class}`}>
          {RATING_COLORS[conv.guest_rating].emoji} {RATING_COLORS[conv.guest_rating].label}
        </span>
      )}
      {conv.column === 'claim_period' && conv.claim_id && (
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs font-semibold text-gray-900">${conv.claim_amount?.toFixed(2)}</span>
          <ClaimStatusBadge status={conv.claim_status} />
        </div>
      )}
      <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onInvoice}
          className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
        >
          📄 Invoice
        </button>
      </div>
    </div>
  );
}

function ClaimStatusBadge({ status }: { status?: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    sent: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors[status || 'draft'] || colors.draft}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function DetailPanel({
  conv,
  properties,
  onClose,
  onSetRating,
  onRefresh,
}: {
  conv: Conversation;
  properties: Property[];
  onClose: () => void;
  onSetRating: (conv: Conversation, rating: string) => void;
  onRefresh: () => void;
}) {
  const [messages, setMessages] = useState<any[]>([]);
  const [claimAmount, setClaimAmount] = useState(String(conv.claim_amount || ''));

  useEffect(() => {
    api.getMessages(conv.id).then(setMessages).catch((err: any) => console.error('Failed to load messages:', err));
  }, [conv.id]);

  const handleFileClaim = async () => {
    const amount = parseFloat(claimAmount);
    if (!amount || amount <= 0) return;
    try {
      await api.createClaim({
        conversation_id: conv.id,
        platform: conv.platform,
        amount,
        reason: 'Damage claim filed from dashboard',
        status: 'draft',
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to file claim:', err);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col animate-slide-in">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">{conv.guest_name}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
      </div>
      <div className="px-4 py-3 border-b border-gray-200 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Property</span>
          <span className="text-gray-900 font-medium">{conv.property_name}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Platform</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${PLATFORM_BADGES[conv.platform]?.class}`}>
            {PLATFORM_BADGES[conv.platform]?.label}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Check-in</span>
          <span className="text-gray-900">{formatDate(conv.check_in)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Check-out</span>
          <span className="text-gray-900">{formatDate(conv.check_out)}</span>
        </div>
        <div className="text-sm font-medium text-gray-600">{daysRemaining(conv, properties)}</div>
      </div>
      {conv.column === 'review_period' && (
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Set Rating</p>
          <div className="flex gap-2">
            {['green', 'yellow', 'red'].map((r) => (
              <button
                key={r}
                onClick={() => onSetRating(conv, r)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  conv.guest_rating === r
                    ? `${RATING_COLORS[r].class} border-current`
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {RATING_COLORS[r].emoji} {RATING_COLORS[r].label}
              </button>
            ))}
          </div>
          {conv.guest_rating && (
            <p className="text-xs text-gray-500 mt-2">
              {conv.guest_rating === 'green' && 'Auto-send 5\u2605 review + request guest review'}
              {conv.guest_rating === 'yellow' && 'No review will be posted'}
              {conv.guest_rating === 'red' && 'Review scheduled for day 13 at 23:59'}
            </p>
          )}
          {conv.review_text && (
            <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-700">
              <p className="font-medium text-gray-500 mb-1">Draft review:</p>
              {conv.review_text}
            </div>
          )}
        </div>
      )}
      {conv.column === 'claim_period' && (
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Damage Claim</p>
          {conv.claim_id ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">${conv.claim_amount?.toFixed(2)}</span>
                <ClaimStatusBadge status={conv.claim_status} />
              </div>
              {conv.claim_reason && (
                <p className="text-xs text-gray-600">{conv.claim_reason}</p>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Amount"
                value={claimAmount}
                onChange={(e) => setClaimAmount(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              />
              <button
                onClick={handleFileClaim}
                className="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-red-700"
              >
                File Claim
              </button>
            </div>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="text-xs font-medium text-gray-500 uppercase mb-3">Conversation</p>
        {messages.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-4">No messages</p>
        )}
        <div className="space-y-2">
          {messages.map((m: any) => (
            <div key={m.id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-xs ${
                m.direction === 'outbound' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'
              }`}>
                {m.content}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 py-3 border-t border-gray-200">
        <a
          href={`/conversations`}
          className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View full conversation &rarr;
        </a>
      </div>
    </div>
  );
}
